import React, { useCallback, useRef, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { isDefined } from "../../../../../../shared/utils/assert";
import { useAsyncOperation } from "../../../../../../shared/utils/utils";
import { BluePrimaryButton, WhitePrimaryButton } from "../../../../common/buttons/primary-button";
import { ErrorMessage } from "../../../../common/error-message";
import { StaticAssets } from "../../../../styles/custom-assets";
import { theme } from "../../../../styles/theme";
import type { DigitsInputRef } from "../options/digits-input";
import { DigitsInput } from "../options/digits-input";

export interface SetCardPincodeProps {
  title: string;
  instructionsText: string;
  confirmationText: string;
  validatePincode: (pincode: string) => Promise<void>;
  updatePincode?: (pincode: string) => void;
  validateButtonTitle?: string;
}

const CardPinCodeLength = 4;

export const SetCardPincode = (props: SetCardPincodeProps) => {
  const {
    validatePincode,
    updatePincode,
    title,
    instructionsText: instructions,
    confirmationText: confirmation,
    validateButtonTitle,
  } = props;
  type PincodeState = "define" | "confirm";

  const { formatMessage } = useIntl();

  const [pincode, setPincode] = useState("");
  const [confirmPincode, setConfirmPincode] = useState("");
  const [pincodeState, setPincodeState] = useState<PincodeState>("define");

  const isDefinePincodeValid = pincode.length === CardPinCodeLength;
  const isConfirmPincodeValid = confirmPincode === pincode;

  const [goToNextStep, loading] = useAsyncOperation(async () => {
    if (pincodeState === "confirm") {
      await validatePincode(pincode);
    } else {
      setPincodeState("confirm");
      resetInput();
    }
  });

  const pincodeInputRef = useRef<DigitsInputRef>(null);
  const resetInput = useCallback(() => {
    pincodeInputRef.current?.reset();
  }, []);

  const definePincode = (code: string, complete: boolean) => {
    // Do not reset pincode once it has been defined
    if (pincode === "") {
      updatePincode?.(code);
    }
    if (complete) {
      setPincode(code);
    }
  };

  const changePinCode = () => {
    setPincode("");
    setPincodeState("define");
    setConfirmPincode("");
    resetInput();
  };

  const pincodeConfig =
    pincodeState === "define"
      ? {
          pincodeInstructions: instructions,
          onIdentifierChange: definePincode,
          isPinCodeValid: isDefinePincodeValid,
          isError: false,
          back: undefined,
        }
      : {
          pincodeInstructions: confirmation,
          onIdentifierChange: (code: string) => setConfirmPincode(code),
          isPinCodeValid: isConfirmPincodeValid,
          isError: confirmPincode.length === CardPinCodeLength && !isConfirmPincodeValid,
          validator: (value: string) => value === pincode,
          back: changePinCode,
        };
  return (
    <Container>
      <Illustration src={StaticAssets.cardPincode} alt="" />
      <PincodeTitle>{title}</PincodeTitle>
      <PincodeInstructions>{pincodeConfig.pincodeInstructions}</PincodeInstructions>

      <PincodeInput
        ref={pincodeInputRef}
        length={CardPinCodeLength}
        onIdentifierChange={pincodeConfig.onIdentifierChange}
        validator={pincodeConfig.validator}
      />
      {pincodeConfig.isError && (
        <StyledErrorMessage>{formatMessage("cardPincode.errorCodesMismatch")}</StyledErrorMessage>
      )}
      <ImportantMessage>{formatMessage("cardPincode.importantMessage")}</ImportantMessage>
      <ButtonContainer>
        <NextButton disabled={!pincodeConfig.isPinCodeValid} showSpinner={loading} size="XS" onClick={goToNextStep}>
          {isDefined(pincodeConfig.back) && isDefined(validateButtonTitle)
            ? validateButtonTitle
            : formatMessage("cardPincode.next")}
        </NextButton>
        {isDefined(pincodeConfig.back) && (
          <PreviousButton size="XS" onClick={pincodeConfig.back}>
            {formatMessage("cardPincode.previous")}
          </PreviousButton>
        )}
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: stretch;
`;

const Illustration = styled.img`
  width: 56px;
  height: 56px;
`;

const PincodeTitle = styled.h2`
  ${theme.mediumText};
  margin: 16px 0 0;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const PincodeInstructions = styled.span`
  ${theme.text};
  margin-top: 8px;
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.gray[800]};
`;

const PincodeInput = styled(DigitsInput)`
  margin-top: 28px;
  align-self: center;
`;

const StyledErrorMessage = styled(ErrorMessage)`
  margin-top: 12px;
  text-align: center;
`;

/* The warning about never sharing the code: quieter than the instructions, still readable. */
const ImportantMessage = styled.span`
  ${theme.text};
  margin-top: 24px;
  font-size: 12px;
  line-height: 16px;
  color: ${theme.colors.gray[700]};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const NextButton = styled(BluePrimaryButton)`
  width: 100%;
  padding: 13px;
  box-shadow: none;
`;

/* Outlined, not a plain white fill: DefaultButton clears the border, so it is set back here. */
const PreviousButton = styled(WhitePrimaryButton)`
  width: 100%;
  padding: 13px;
  border: 1px solid ${theme.colors.blue[700]};
  box-shadow: none;
  color: ${theme.colors.blue[700]};

  &:focus,
  &:active,
  &:hover {
    background-color: ${theme.colors.gray[50]};
  }
`;
